import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukWhen } from "@/lib/time";
import { Btn, CellMain, CellSub, Empty, Panel, St, Tb, Td, Th } from "@/components/portal/crm/kit";
import { toast } from "sonner";

const num = { fontVariantNumeric: "tabular-nums" };

/** The monthly "here's what you owe your floor" note — sent automatically on the 1st, or now. */
export function PayoutNotes() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/refer/seats/payout-notes").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const notes = d.notes || [];

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/refer/seats/payout-notes/send");
      toast.success(`${r.data.corrected ? "Corrected note" : "Note"}${r.data.emailed === 1 ? "" : "s"} sent · ${gbp(r.data.total_gbp)} across ${r.data.agents} agent${r.data.agents === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send those"); }
    finally { setBusy(false); }
  };

  return (
    <Panel title={`What you owe your floor · ${d.period}`} pad={false} testId="payout-notes"
           sub={d.auto}
           footer={<span>Each agent gets their own note with the cases behind it, and you get one copy of the lot. Connexis pays your company — this is the internal split you settle with them.</span>}
           right={notes.length ? (
             <Btn sm kind="primary" disabled={busy} onClick={send} testId="payout-notes-send">
               {busy ? "Sending…" : d.changed ? "Send the corrected note" : d.sent ? "Send again" : "Send now"}
             </Btn>
           ) : null}>
      {d.changed && (
        <div className="warn" data-testid="payout-notes-changed" style={{ margin: 0, borderRadius: 0 }}>
          <span>
            <b>These figures have moved since the notes went out.</b> Your agents are holding an older number.
            Connexis sends the correction on its own at the next run, or you can send it now.
          </span>
        </div>
      )}
      {notes.length === 0 ? (
        <Empty testId="payout-notes-empty">
          Nothing owed for {d.period} yet. A note goes out the moment an agent on a share has a case fund.
        </Empty>
      ) : (
        <Tb testId="payout-notes-table" head={<>
          <Th style={{ width: "36%" }}>Agent</Th>
          <Th className="r" style={{ width: "12%" }}>Share</Th>
          <Th className="r" style={{ width: "12%" }}>Cases</Th>
          <Th className="r" style={{ width: "20%" }}>You owe</Th>
          <Th style={{ width: "20%" }}>Note</Th>
        </>}>
          {notes.map((n) => (
            <tr key={n.seat_id} data-testid={`payout-note-${n.seat_id}`}>
              <Td className="lead">
                <CellMain>{n.name}</CellMain>
                <CellSub>{n.email}</CellSub>
              </Td>
              <Td className="r"><CellMain style={num}>{n.split_pct}%</CellMain></Td>
              <Td className="r"><CellMain style={num}>{n.cases}</CellMain></Td>
              <Td className="r">
                <CellMain style={{ ...num, fontWeight: 800 }}>{gbp(n.agent_gbp)}</CellMain>
                {n.owed_gbp ? <CellSub style={{ textAlign: "right" }}>{gbp(n.owed_gbp)} not settled with you yet</CellSub> : null}
              </Td>
              <Td>
                {d.changed
                  ? <><St tone="quiet">Needs resending</St><CellSub style={{ marginTop: 4 }}>figures moved</CellSub></>
                  : d.sent
                    ? <><St tone="mint">Sent</St><CellSub style={{ marginTop: 4 }}>{ukWhen(d.sent.sent_at)}</CellSub></>
                    : <St tone="ink">Goes on the 1st</St>}
              </Td>
            </tr>
          ))}
          <tr data-testid="payout-notes-total">
            <Td className="lead"><CellMain>Total to settle</CellMain></Td>
            <Td /><Td />
            <Td className="r"><CellMain style={{ ...num, fontWeight: 800 }}>{gbp(d.total_gbp)}</CellMain></Td>
            <Td />
          </tr>
        </Tb>
      )}
    </Panel>
  );
}
